import React, { useState } from "react";
import { MicOff } from "lucide-react";

/**
 * MicBlockedPanel — shown when the browser has explicitly denied microphone access.
 * Walks the user through unblocking on Chrome, Edge, Safari, iOS.
 * Tries to re-prompt first; if that fails (because the site is hard-blocked at
 * browser level, where getUserMedia rejects silently), expands a clear how-to.
 */
export default function MicBlockedPanel() {
  const [open, setOpen] = useState(false);
  const [tried, setTried] = useState(false);
  const [reason, setReason] = useState("");

  const tryFix = async (e) => {
    e.stopPropagation();
    setTried(true);
    setReason("");
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      s.getTracks().forEach(t => t.stop());
      // Worked! Reload so all hooks re-init clean.
      window.location.reload();
    } catch (err) {
      // Most common: NotAllowedError when site is hard-blocked.
      setReason(err?.name || "Unknown");
      setOpen(true);
    }
  };

  const browser = detectBrowser();

  return (
    <>
      <div className="stage-controls">
        <button
          onClick={() => setOpen(o => !o)}
          className="rc-btn !text-[10px] !py-1 !px-2 flex items-center gap-1 border-red-500/60 text-red-400"
          data-testid="mic-blocked-recover"
        >
          <MicOff size={12} /> MIC BLOCKED · TAP TO FIX
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="mic-blocked-modal"
        >
          <div
            className="rc-panel max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rc-label text-[10px] mb-2 opacity-70">// MICROPHONE · BLOCKED</div>
            <div className="font-display text-xl rc-glow-text mb-3">Your browser is blocking my ears, sir.</div>
            <div className="text-xs text-[var(--rc-text)] mb-4 leading-relaxed">
              The site permission is set to <span className="text-red-400">Blocked</span>. The global setting being "Sites can ask" isn't enough — I need <i>this</i> site (Raw Company) added to the <b>Allowed</b> list specifically.
            </div>

            {tried && reason && (
              <div className="text-[10px] text-red-400 mb-3 border border-red-500/40 px-2 py-1.5">
                Auto-fix failed: {reason}. Follow the steps below — takes 30 seconds.
              </div>
            )}

            <div className="border border-[var(--rc-gold)] p-3 mb-4">
              <div className="rc-label text-[10px] mb-2 opacity-80">{browser.label} · INSTRUCTIONS</div>
              <ol className="text-xs text-[var(--rc-text)] space-y-1.5 list-decimal list-inside leading-relaxed">
                {browser.steps.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            </div>

            <button
              onClick={tryFix}
              className="rc-btn w-full !py-2.5"
              data-testid="mic-retry"
            >
              I'VE ALLOWED IT — RETRY NOW
            </button>

            <div className="rc-label text-[9px] mt-3 opacity-50">
              Still stuck? Try the <b>tap-to-talk</b> mode — just tap the gold orb directly. That uses a different audio pipeline that often works even when continuous listening doesn't.
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function detectBrowser() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  const isiOS = /iPad|iPhone|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);
  const isEdge = /Edg/.test(ua);
  const isFirefox = /Firefox/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome|Chromium|Edg/.test(ua);
  const isChrome = /Chrome|Chromium/.test(ua) && !isEdge;

  if (isiOS) {
    return {
      label: "iPHONE · SAFARI",
      steps: [
        "Open the iPhone Settings app (grey cog icon).",
        "Tap Safari → scroll to Settings for Websites → Microphone.",
        "Find this site in the list and set it to ALLOW. (Or set the global default to Allow.)",
        "Come back to this Safari tab, pull down to refresh, then tap the gold orb to talk.",
      ],
    };
  }
  if (isAndroid) {
    return {
      label: "ANDROID · CHROME",
      steps: [
        "Tap the three-dot menu (top right of Chrome).",
        "Tap Site settings → Microphone.",
        "Find this site and switch it to Allow.",
        "Reload the tab, then tap the gold orb.",
      ],
    };
  }
  if (isEdge) {
    return {
      label: "EDGE · WINDOWS",
      steps: [
        "Click the padlock icon (🔒) to the LEFT of the URL in the address bar.",
        "Click \"Permissions for this site\".",
        "Find Microphone in the list → change the dropdown to Allow.",
        "Close this tab completely and reopen the URL (essential — Edge caches the old setting).",
        "Tap the gold orb to speak.",
      ],
    };
  }
  if (isFirefox) {
    return {
      label: "FIREFOX",
      steps: [
        "Click the padlock icon to the left of the URL.",
        "Click the small \">\" next to \"Connection secure\".",
        "Find Microphone → click the small X to clear the blocked permission.",
        "Reload the page and tap the gold orb — Firefox will ask again, click Allow.",
      ],
    };
  }
  if (isSafari) {
    return {
      label: "SAFARI · MAC",
      steps: [
        "Safari menu (top-left) → Settings → Websites tab → Microphone.",
        "Find this site in the list and set it to Allow.",
        "Close all Safari tabs of this site and reopen.",
        "Tap the gold orb to talk.",
      ],
    };
  }
  // Chrome / default
  return {
    label: "CHROME",
    steps: [
      "Click the padlock icon (🔒) on the left of the URL in the address bar.",
      "Click \"Site settings\".",
      "Find Microphone → change the dropdown to Allow.",
      "Reload the page and tap the gold orb to speak.",
    ],
  };
}
